// Libraries
import { VictoryLine } from 'victory';
// Utils
import { colors } from '@theme/foundations';
// Types
import { RatingKeys, RatingRanges } from '@globalTypes/index';

/**
 * Renders VictoryLine components representing the rating ranges as vertical lines on the chart.
 *
 * @function
 * @param {RatingRanges} ratingRanges - The rating ranges used to determine the positions and colors of the lines.
 * @param {number | string} [xLimit] - The x-coordinate at which the vertical lines should be drawn. If not provided,
 *                                      the lines will be drawn at the leftmost position of the chart.
 * @returns {ReactElement[]} - An array of React elements representing the rendered VictoryLines for the rating ranges.
 * Important Note: This implementation relies on the Victory library, which does not
 * support creating this
 * component as a separate module due to potential injection vulnerabilities. Please avoid
 * using this
 * function where possible.
 *
 * For more information, refer to the documentation:
 * https://formidable.com/open-source/victory/docs
 */
export const renderRatingRanges = (ratingRanges: RatingRanges, xLimit?: number | string) => {
  return Object.keys(ratingRanges).map((key, index) => {
    // Extract the y-values from the rating ranges object
    const [start, end] = ratingRanges[key as RatingKeys];

    return (
      <VictoryLine
        key={key}
        name={`${key}${index}`}
        data={[
          // Set the x-coordinate to xLimit or the leftmost position of the chart if xLimit is not provided
          { x: xLimit, y: start },
          { x: xLimit, y: end },
        ]}
        style={{
          // Customize the line's color and strokeWidth based on the rating key
          data: { stroke: colors.ratings[key as RatingKeys].dark, strokeWidth: 5 },
        }}
      />
    );
  });
};
